import * as React from "react";
import { connect } from "react-redux";
import { Container, Loader } from "semantic-ui-react";
import axios from "axios";

import { AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import * as iaptQuestionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/questionIds";
import { masterConfig as iaptMasterConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/masterConfig";
import { MultiButtonQuestion, YesOption } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import * as iaptPhq9QuestionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/phq9QuestionIds";
import { masterConfig as iaptPhq9MasterConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/phq9MasterConfig";
import * as iaptGad7QuestionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/gad7QuestionIds";
import { masterConfig as iaptGad7MasterConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/gad7MasterConfig";
import * as iaptWsasQuestionIds from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/wsasQuestionIds";
import { masterConfig as iaptWsasMasterConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/referral/IAPT/wsasMasterConfig";

import { iaptQuestionnaireAnswerOptions, iaptQuestionnaireWsasAnswerOptions } from "../models";
import { ApplicationState } from "../../applicationState";
import { ClinicalNotesApi } from "../ClinicalNotesApi";
import { OhCaseDetailModel } from "../../crud/ohCase/model";
import { ohCaseApi } from "../../crud/ohCase";

import { questionnaireKey } from "./ReferralClinicalNotesSectionsV4";

interface StateProps {
    customerId: string;
    ohCaseId: string;
    taskId: string;
}

const IaptViewCompletedQuestionnaireUnconnected: React.FunctionComponent<StateProps> = (props) => {
    const [questionnaireLoaded, setQuestionnaireLoaded] = React.useState(false);
    const [questions, setQuestions] = React.useState<AnsweredQuestionWithId[]>([]);
    const [ohCase, setOhCase] = React.useState<OhCaseDetailModel>();

    React.useEffect(() => {
        (async () => {
            const savedOhCase = await ohCaseApi.detail(props.customerId, props.ohCaseId);
            setOhCase(savedOhCase);

            const task = savedOhCase.tasks.find(t => t.id === props.taskId);

            const clinicalNotesApi = new ClinicalNotesApi(axios);
            const savedQuestions = (await clinicalNotesApi.load(props.ohCaseId, task.precedingTaskId))?.data.questions;
            setQuestions(savedQuestions);

            setQuestionnaireLoaded(true);
        })();
    }, []);

    const loadQuestionnaire = () => {
        // Do nothing
    };

    const submitQuestionnaire = async (): Promise<boolean> => {
        // Do nothing
        return false;
    };

    return (
        <Container>
            {!questionnaireLoaded ?
                (<Loader active />) :
                (
                    <OldProgressBarQuestionnaireContainer
                        loadQuestionnaire={loadQuestionnaire}
                        questionnaireKey={questionnaireKey}
                        readonly={true}
                        submitQuestionnaire={submitQuestionnaire}
                        questionnaireType={QuestionnaireTypes.Other}
                    >
                        <OldProgressBarQuestionnaireSection
                            id="PersonalInformation"
                            title="Personal Information"
                            questionnaireKey={questionnaireKey}
                            readonly={true}
                        >
                            <TextQuestion
                                inline
                                hideDivider
                                readonly={true}
                                questionConfig={({
                                    id: "IaptEmployeeName",
                                    questionText: "Employee Name"
                                })}
                                answer={`${ohCase.employeeFirstName} ${ohCase.employeeLastName}`}
                            />
                            <TextQuestion
                                inline
                                hideDivider
                                readonly={true}
                                questionConfig={({
                                    id: "IaptJobRole",
                                    questionText: "Job Role"
                                })}
                                answer={ohCase.jobTitle}
                            />
                        </OldProgressBarQuestionnaireSection>
                        <OldProgressBarQuestionnaireSection
                            id="PHQ9"
                            title="PHQ9"
                            questionnaireKey={questionnaireKey}
                            readonly={true}
                        >
                            <MultiButtonQuestion
                                hideDivider
                                inline
                                readonly={true}
                                questionConfig={getQuestionConfig(iaptMasterConfig, iaptQuestionIds.IaptCarryOutPhq9Assessment)}
                                answer={getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutPhq9Assessment)}
                            />
                            {getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutPhq9Assessment) === YesOption &&
                                <>
                                    <h4>Over the last 2 weeks, how often have you been bothered by any of the following problems?</h4>
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptLittleInterestOrPleasureInDoingThings)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptLittleInterestOrPleasureInDoingThings)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptFeelingDownDepressedOrHopeless)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptFeelingDownDepressedOrHopeless)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(
                                            iaptPhq9MasterConfig,
                                            iaptPhq9QuestionIds.IaptTroubleFallingOrStayingAsleepOrSleepingTooMuch)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            iaptPhq9QuestionIds.IaptTroubleFallingOrStayingAsleepOrSleepingTooMuch)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptFeelingTiredOrHavingLittleEnergy)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptFeelingTiredOrHavingLittleEnergy)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptPoorAppetiteOrOvereating)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptPoorAppetiteOrOvereating)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptFeelingBadAboutYourself)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptFeelingBadAboutYourself)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(
                                            iaptPhq9MasterConfig,
                                            iaptPhq9QuestionIds.IaptTroubleConcentratingOnReadingOrWatching)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            iaptPhq9QuestionIds.IaptTroubleConcentratingOnReadingOrWatching)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptPhq9MasterConfig, iaptPhq9QuestionIds.IaptMovingOrSpeakingSoSlowly)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptPhq9QuestionIds.IaptMovingOrSpeakingSoSlowly)}
                                    />
                                    <MultiButtonQuestion
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(
                                            iaptPhq9MasterConfig,
                                            iaptPhq9QuestionIds.IaptThoughtsThatWouldBeBetterOffDeadOrOfHurtingYourself)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            iaptPhq9QuestionIds.IaptThoughtsThatWouldBeBetterOffDeadOrOfHurtingYourself)}
                                    />
                                    <TextQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={({
                                            id: "IaptPsychologicalWellbeingPHQ9Score",
                                            questionText: "PHQ9 Score (These comments will be added to your report)"
                                        })}
                                        questionClassName={"question-red"}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            "PsychologicalWellbeingPHQ9Score")}
                                    />
                                </>
                            }
                        </OldProgressBarQuestionnaireSection>
                        <OldProgressBarQuestionnaireSection
                            id="GAD7"
                            title="GAD7"
                            questionnaireKey={questionnaireKey}
                            readonly={true}
                        >
                            <MultiButtonQuestion
                                hideDivider
                                inline
                                readonly={true}
                                questionConfig={getQuestionConfig(iaptMasterConfig, iaptQuestionIds.IaptCarryOutGad7Assessment)}
                                answer={getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutGad7Assessment)}
                            />
                            {getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutGad7Assessment) === YesOption &&
                                <>
                                    <h4>Over the last 2 weeks, how often have you been bothered by any of the following problems?</h4>
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptFeelingNervousAnxiousOrOnEdge)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptGad7QuestionIds.IaptFeelingNervousAnxiousOrOnEdge)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptNotBeingAbleToStopOrControlWorrying)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptGad7QuestionIds.IaptNotBeingAbleToStopOrControlWorrying)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptWorryingTooMuchAboutDifferentThings)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptGad7QuestionIds.IaptWorryingTooMuchAboutDifferentThings)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptTroubleRelaxing)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptGad7QuestionIds.IaptTroubleRelaxing)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptBeingSoRestlessThatItIsHardToSitStill)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptGad7QuestionIds.IaptBeingSoRestlessThatItIsHardToSitStill)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptGad7MasterConfig, iaptGad7QuestionIds.IaptBecomingEasilyAnnoyedOrIrritable)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            iaptGad7QuestionIds.IaptBecomingEasilyAnnoyedOrIrritable)}
                                    />
                                    <MultiButtonQuestion
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(
                                            iaptGad7MasterConfig,
                                            iaptGad7QuestionIds.IaptFeelingAfraidAsIfSomethingAwfulMightHappen)}
                                        values={iaptQuestionnaireAnswerOptions}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            iaptGad7QuestionIds.IaptFeelingAfraidAsIfSomethingAwfulMightHappen)}
                                    />
                                    <TextQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={({
                                            id: "PsychologicalWellbeingGAD7Score",
                                            questionText: "GAD7 Score (These comments will be added to your report)"
                                        })}
                                        questionClassName={"question-red"}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            "PsychologicalWellbeingGAD7Score")}
                                    />
                                </>
                            }
                        </OldProgressBarQuestionnaireSection>
                        <OldProgressBarQuestionnaireSection
                            id="WSAS"
                            title="WSAS"
                            questionnaireKey={questionnaireKey}
                            readonly={true}
                        >
                            <MultiButtonQuestion
                                hideDivider
                                inline
                                readonly={true}
                                questionConfig={getQuestionConfig(iaptMasterConfig, iaptQuestionIds.IaptCarryOutWsasAssessment)}
                                answer={getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutWsasAssessment)}
                            />
                            {getAnswerToQuestionWithId(questions, iaptQuestionIds.IaptCarryOutWsasAssessment) === YesOption &&
                                <>
                                    <h4>People's problems sometimes affect their ability to do certain day-to-day tasks in their lives.
                                        To rate your problems, look at each section and determine on the scale provided how much your problem impairs
                                        your ability to carry out the activity.</h4>
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptWsasMasterConfig, iaptWsasQuestionIds.IaptWork)}
                                        values={iaptQuestionnaireWsasAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptWsasQuestionIds.IaptWork)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptWsasMasterConfig, iaptWsasQuestionIds.IaptHomeManagement)}
                                        values={iaptQuestionnaireWsasAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptWsasQuestionIds.IaptHomeManagement)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptWsasMasterConfig, iaptWsasQuestionIds.IaptSocialLeisureActivities)}
                                        values={iaptQuestionnaireWsasAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptWsasQuestionIds.IaptSocialLeisureActivities)}
                                    />
                                    <MultiButtonQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptWsasMasterConfig, iaptWsasQuestionIds.IaptPrivateLeisureActivities)}
                                        values={iaptQuestionnaireWsasAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptWsasQuestionIds.IaptPrivateLeisureActivities)}
                                    />
                                    <MultiButtonQuestion
                                        inline
                                        readonly={true}
                                        questionConfig={getQuestionConfig(iaptWsasMasterConfig, iaptWsasQuestionIds.IaptFamilyAndRelationships)}
                                        values={iaptQuestionnaireWsasAnswerOptions}
                                        answer={getAnswerToQuestionWithId(questions, iaptWsasQuestionIds.IaptFamilyAndRelationships)}
                                    />
                                    <TextQuestion
                                        hideDivider
                                        inline
                                        readonly={true}
                                        questionConfig={({
                                            id: "PsychologicalWellbeingWSASScore",
                                            questionText: "WSAS Score (These comments will be added to your report)"
                                        })}
                                        questionClassName={"question-red"}
                                        answer={getAnswerToQuestionWithId(
                                            questions,
                                            "PsychologicalWellbeingWSASScore")}
                                    />
                                </>
                            }
                        </OldProgressBarQuestionnaireSection>
                    </OldProgressBarQuestionnaireContainer>
                )
            }
        </Container>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const customerId = state.router.params?.customerId;
    const ohCaseId = state.router.params?.ohCaseId;
    const taskId = state.router.params.taskId;

    return {
        customerId,
        ohCaseId,
        taskId
    };
};

export const IaptViewCompletedQuestionnaire = connect(mapStateToProps)(IaptViewCompletedQuestionnaireUnconnected);
